import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'

/**
 * The two list dropdowns: pick several, or pick one.
 *
 * Both used to carry their own copy of the anchored-panel logic. That copy has moved to
 * Popover.jsx, which the date and time pickers share — and which fixes the difference that
 * mattered: this file used to close on Escape without stopping the event, so pressing Escape
 * on a dropdown inside a dialog closed the dialog too, losing whatever had been typed into it.
 *
 * The other thing missing here was any keyboard at all. `role="menu"` sat on a panel of plain
 * buttons, so a list of ninety-six times was ninety-six tab stops and nothing else. Arrow keys,
 * Home/End and Enter now work in both, with one tab stop for the list.
 */

/** A search box pinned to the top of a panel — only worth its space once a list is long
 *  enough that scanning it beats typing a few letters. */
function PanelSearch({ value, onChange, inputRef }) {
  return (
    <div className="filter-panel-search">
      <Icon name="search" size={13} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder="Search…"
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}

/**
 * Arrow-key navigation over a list of options.
 *
 * Kept as a hook rather than written twice, because the two dropdowns below differ only in
 * what activating a row does — toggle it, or choose it and close.
 */
function useListKeys(count, onActivate, deps) {
  const [active, setActive] = useState(0)
  const listRef = useRef(null)
  const searchRef = useRef(null)

  // Back to the top whenever the list itself changes underneath, so the highlight is never
  // left pointing past the end of a filtered list.
  useEffect(() => { setActive(0) }, deps)

  useEffect(() => {
    listRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(count - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0) }
    else if (e.key === 'End') { e.preventDefault(); setActive(count - 1) }
    else if (e.key === 'Enter') { e.preventDefault(); onActivate(active) }
  }

  return { active, setActive, listRef, searchRef, onKeyDown }
}

/**
 * A trigger button that opens a checkable list anchored beneath it — the same row style as a
 * right-click menu's checked items. Stays open across clicks so several options can be toggled
 * in one visit; see PickerDropdown for the "pick one and close" sibling.
 */
export function MultiSelectDropdown({
  value,
  onToggle,
  onClear,
  options,
  placeholder = 'Filter',
  summary = false,
  searchable = false,
  // A caller that wants more than a line of text in the trigger — icons beside each chosen
  // option, say — supplies it here and gets the same panel and the same behaviour.
  renderTrigger = null,
  align = 'right',
  // For a filter that does not apply to what is on screen — disabled rather than hidden, with
  // the reason as its tooltip, so it stays where people look for it.
  disabled = false,
  title,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const anchorRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => { if (!open) setQuery('') }, [open])
  // Disabled while open — the page moved to where this filter does not apply — closes it.
  useEffect(() => { if (disabled) setOpen(false) }, [disabled])

  const chosen = options.filter((o) => value.has(o.value))
  const label = chosen.length === 0
    ? placeholder
    : summary
      ? `${placeholder} (${chosen.length})`
      : chosen.map((o) => o.label).join(', ')
  const needle = query.trim().toLowerCase()
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options

  const keys = useListKeys(shown.length, (i) => shown[i] && onToggle(shown[i].value), [needle, open])

  return (
    <div className="filter-dropdown" ref={anchorRef}>
      <button
        type="button"
        className={`btn sm filter-trigger ${chosen.length && !disabled ? 'active' : ''} ${renderTrigger ? 'custom' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        title={title}
      >
        {renderTrigger ? renderTrigger(chosen) : <span className="filter-trigger-label">{label}</span>}
        <Icon name="down" size={12} strokeWidth={2.2} />
      </button>

      <Popover
        open={open}
        anchorRef={anchorRef}
        close={close}
        className="filter-panel"
        role="listbox"
        label={placeholder}
        align={align}
        matchWidth
        // Filtering changes the panel's height, so where it sits has to be worked out again.
        placeKey={needle}
        onKeyDown={keys.onKeyDown}
        autoFocus
      >
        {searchable && <PanelSearch value={query} onChange={setQuery} inputRef={keys.searchRef} />}
        <div className="dd-items" ref={keys.listRef} tabIndex={-1}>
          {shown.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={value.has(o.value)}
              data-i={i}
              className={`ctx-item ${i === keys.active ? 'active' : ''}`}
              onMouseEnter={() => keys.setActive(i)}
              onClick={() => onToggle(o.value)}
            >
              <span className="ctx-item-check">
                {value.has(o.value) && <Icon name="check" size={13} strokeWidth={2.4} />}
              </span>
              <span>{o.label}</span>
            </button>
          ))}
          {shown.length === 0 && <div className="ctx-label">Nothing matches</div>}
          {onClear && chosen.length > 0 && (
            <>
              <div className="ctx-sep" />
              <button type="button" className="ctx-item" onClick={onClear}>Clear</button>
            </>
          )}
        </div>
      </Popover>
    </div>
  )
}

/**
 * One choice from a short list, opened under a trigger — the same popover as
 * MultiSelectDropdown, but for a single pick (a PM's assistant) or a one-shot add (a new crew
 * member) rather than several at once, so choosing an option closes it immediately instead of
 * leaving it open to keep toggling.
 */
export function PickerDropdown({
  value,
  options,
  onPick,
  placeholder = 'Choose…',
  searchable = false,
  disabled = false,
  // The same two the multi-select has: a caller wanting more than a line of text in the
  // trigger, and a panel that opens leftward for a control at the left of its row.
  renderTrigger = null,
  align = 'right',
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const anchorRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => { if (!open) setQuery('') }, [open])

  const current = options.find((o) => o.value === value)
  const needle = query.trim().toLowerCase()
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options

  const choose = (v) => { onPick(v); setOpen(false) }
  const keys = useListKeys(shown.length, (i) => shown[i] && choose(shown[i].value), [needle, open])

  return (
    <div className="filter-dropdown" ref={anchorRef}>
      <button
        type="button"
        className={`btn sm filter-trigger ${current?.value ? 'active' : ''} ${renderTrigger ? 'custom' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {renderTrigger
          ? renderTrigger(current)
          : <span className="filter-trigger-label">{current ? current.label : placeholder}</span>}
        <Icon name="down" size={12} strokeWidth={2.2} />
      </button>

      <Popover
        open={open}
        anchorRef={anchorRef}
        close={close}
        className="filter-panel"
        role="listbox"
        label={placeholder}
        align={align}
        matchWidth
        placeKey={needle}
        onKeyDown={keys.onKeyDown}
        autoFocus
      >
        {searchable && <PanelSearch value={query} onChange={setQuery} inputRef={keys.searchRef} />}
        <div className="dd-items" ref={keys.listRef} tabIndex={-1}>
          {shown.map((o, i) => (
            <button
              key={o.value || '_none'}
              type="button"
              role="option"
              aria-selected={o.value === value}
              data-i={i}
              className={`ctx-item ${i === keys.active ? 'active' : ''}`}
              onMouseEnter={() => keys.setActive(i)}
              onClick={() => choose(o.value)}
            >
              <span className="ctx-item-check">
                {o.value === value && <Icon name="check" size={13} strokeWidth={2.4} />}
              </span>
              <span>{o.label}</span>
            </button>
          ))}
          {shown.length === 0 && <div className="ctx-label">Nothing matches</div>}
        </div>
      </Popover>
    </div>
  )
}
